import type { CharacterId } from '../questions/types';

/**
 * Character bonds (plan doc 14).
 *
 * A bond is the measure of how much time a learner has spent with one
 * character, and it only moves in that character's own domain: answering a
 * harmony question builds Otto, not Clara. That is the whole design — the
 * reward for going deep on one subject is that its host gets to know you.
 */

/** Points to reach each level. Level 5 is the top. */
export const BOND_LEVELS: readonly number[] = [100, 300, 700, 1500, 3000];

export const MAX_BOND_LEVEL = BOND_LEVELS.length;

/** Gems paid for reaching a level. */
export const BOND_LEVEL_GEMS = 50;

/** What each action is worth (plan doc 14). */
export const BOND_POINTS = {
  /** A correct exercise in the character's domain. */
  correct: 1,
  /** A microphone exercise passed on their instrument track. */
  micPass: 3,
  /** A lesson completed in one of their units. */
  lesson: 10,
  /** Taking a lesson to crown 5. */
  crownFive: 25,
  /** A legendary run. */
  legendary: 50,
} as const;

export function bondLevel(points: number): number {
  let level = 0;
  for (const threshold of BOND_LEVELS) {
    if (points >= threshold) level += 1;
    else break;
  }
  return level;
}

export interface BondView {
  character: CharacterId;
  points: number;
  level: number;
  /** Points the next level needs, or null at the top. */
  nextAt: number | null;
  /** 0–1 towards the next level, 1 at the top. */
  fraction: number;
}

export function bondView(character: CharacterId, points: number): BondView {
  const level = bondLevel(points);
  const nextAt = level >= MAX_BOND_LEVEL ? null : (BOND_LEVELS[level] ?? null);
  const previous = level === 0 ? 0 : (BOND_LEVELS[level - 1] ?? 0);

  return {
    character,
    points,
    level,
    nextAt,
    fraction:
      nextAt === null ? 1 : Math.max(0, Math.min(1, (points - previous) / (nextAt - previous))),
  };
}

/** Gems owed for going from one level to another. */
export function bondGems(fromLevel: number, toLevel: number): number {
  return Math.max(0, toLevel - fromLevel) * BOND_LEVEL_GEMS;
}

export interface BondFacts {
  /** The character who hosts this lesson or unit. */
  host: CharacterId | null;
  /** Hosts of the individual questions, for a mixed practice session. */
  questionHosts?: readonly CharacterId[];
  correct: number;
  micPasses?: number;
  /** True when a whole lesson in their unit was finished. */
  lessonCompleted?: boolean;
  crownReached?: number;
  legendary?: boolean;
}

/**
 * Bond points a session earns, by character.
 *
 * A practice session draws from several domains, so the correct answers are
 * split by whoever hosts each question rather than all going to one character.
 * Anything not attributable goes to the session's host, and nothing at all goes
 * nowhere — a point earned should always land somewhere.
 */
export function bondPoints(facts: BondFacts): Partial<Record<CharacterId, number>> {
  const out: Partial<Record<CharacterId, number>> = {};
  const add = (character: CharacterId | null, amount: number): void => {
    if (character === null || amount <= 0) return;
    out[character] = (out[character] ?? 0) + amount;
  };

  const hosts = facts.questionHosts ?? [];
  if (hosts.length > 0) {
    for (const host of hosts) add(host, BOND_POINTS.correct);
  } else {
    add(facts.host, facts.correct * BOND_POINTS.correct);
  }

  add(facts.host, (facts.micPasses ?? 0) * BOND_POINTS.micPass);
  if (facts.lessonCompleted === true) add(facts.host, BOND_POINTS.lesson);
  if (facts.crownReached === 5) add(facts.host, BOND_POINTS.crownFive);
  if (facts.legendary === true) add(facts.host, BOND_POINTS.legendary);

  return out;
}
